import { useEffect, useMemo, useRef, useState } from 'react'
import AvatarCropper from './AvatarCropper'
import { THEMES, getNextLockedThemeId } from '../utils/themes'
import { getDailyCompletions } from '../utils/storage'
import { IconTicket, IconClapper, IconLock, IconCheck, IconFlame, IconPencil, IconCamera, IconSignOut, IconDownload, IconTrash } from './icons'
import { db } from '../firebase'
import { Capacitor } from '@capacitor/core'
import { doc, getDoc } from 'firebase/firestore'

// ─── Bar chart (Box Office Numbers) ──────────────────────────────────────────
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function BarChart({ dates, completions, todayDate, maxMoves }) {
  const W = 400, H = 132
  const padL = 40, padR = 18, padT = 18, padB = 34
  const plotW = W - padL - padR
  const plotH = H - padT - padB

  // Y domain: actual data max + 1 so the tallest bar never clips at the top.
  const yMax  = maxMoves + 1
  const baseY = padT + plotH
  const yFor  = (v) => padT + (1 - v / yMax) * plotH

  // Centered slots so each bar sits under its day label.
  const slotW   = plotW / dates.length
  const barW    = Math.min(slotW * 0.55, 28)
  const xCenter = (i) => padL + (i + 0.5) * slotW

  // One tick per whole number from 1 up to yMax (exclusive) so labels always
  // land on integers. yMax = data max + 1, so the very top value isn't labelled.
  const refs = []
  for (let v = 1; v < yMax; v++) {
    refs.push({ value: v, y: yFor(v) })
  }

  const bars = dates.map((date, i) => {
    const entry = completions[date]
    return {
      i,
      date,
      moves: entry?.moves ?? null,
      cx: xCenter(i),
      isToday: date === todayDate,
      label: SHORT_DAYS[new Date(date + 'T00:00:00').getDay()],
    }
  })

  const missedY = padT + plotH + 4

  return (
    <div className="stats-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="stats-chart__svg" role="img" aria-label="Last 7 days of hops">
        {refs.map((r) => (
          <g key={r.value}>
            <line x1={padL} x2={W - padR} y1={r.y} y2={r.y} className="stats-chart__grid" />
            <text x={padL - 8} y={r.y + 3} className="stats-chart__ylabel" textAnchor="end">{r.value}</text>
          </g>
        ))}
        {bars.map((b) => {
          if (b.moves == null) {
            // SVG X-mark — NOT emoji. Color emoji never renders in SVG <text> on
            // WKWebView, so we draw two crossed lines where the cross-mark sat.
            return (
              <g key={b.date} stroke="#e06c6c" strokeWidth="2" strokeLinecap="round">
                <line x1={b.cx - 4} y1={missedY + 2} x2={b.cx + 4} y2={missedY + 10} />
                <line x1={b.cx - 4} y1={missedY + 10} x2={b.cx + 4} y2={missedY + 2} />
              </g>
            )
          }
          const top = yFor(b.moves)
          return (
            <rect
              key={b.date}
              x={b.cx - barW / 2}
              y={top}
              width={barW}
              height={baseY - top}
              rx={4}
              className={b.isToday ? 'stats-chart__bar stats-chart__bar--today' : 'stats-chart__bar'}
            />
          )
        })}
        {bars.map((b) => (
          <text key={`xl-${b.date}`} x={b.cx} y={H - 10} className="stats-chart__xlabel" textAnchor="middle">{b.label}</text>
        ))}
      </svg>
    </div>
  )
}

// Theme accent swatches — static values shown on unlocked cards
function formatJoined(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', year: 'numeric',
  })
}

// ─── Data report (printable HTML export) ─────────────────────────────────────
// Theme keys → readable display names for the export.
const REPORT_THEME_NAMES = {
  extra: 'Extra',
  understudy: 'Understudy',
  supporting: 'Supporting Role',
  leading: 'Leading Role',
  award: 'Award Season',
  alist: 'A-List',
  oscar: 'Oscar Night',
}

// Escape user-provided strings before injecting them into the report HTML.
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))
}

// Firestore Timestamp (instance, or a raw { seconds, nanoseconds }) → JS Date.
function tsToDate(ts) {
  if (!ts) return null
  if (typeof ts.toDate === 'function') return ts.toDate()
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000)
  return null
}
const fmtDate = (ts) => { const d = tsToDate(ts); return d ? d.toLocaleDateString() : '—' }
const fmtDateTime = (ts) => { const d = tsToDate(ts); return d ? d.toLocaleString() : '—' }

// A daily entry's time → "m:ss" (prefer the stored display string).
function fmtDailyTime(entry) {
  if (typeof entry?.time === 'string' && entry.time) return entry.time
  const secs = entry?.timeSeconds ?? (typeof entry?.time === 'number' ? entry.time : null)
  if (secs == null) return '—'
  const m = Math.floor(secs / 60)
  const s = Math.round(secs % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

// Build the full, self-contained HTML report from the user's Firestore doc.
function buildReportHtml(data, generatedAt) {
  const genDate = generatedAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  const num = (v) => Number(v) || 0

  const themeKeys = Array.isArray(data.permUnlockedThemes) ? data.permUnlockedThemes : []
  const themeItems = themeKeys.length
    ? themeKeys.map((k) => `<li>${escapeHtml(REPORT_THEME_NAMES[k] || k)}</li>`).join('')
    : '<li class="empty-inline">None unlocked</li>'

  const dailies = (data.completedDailies && typeof data.completedDailies === 'object') ? data.completedDailies : {}
  const dailyRows = Object.entries(dailies)
    .sort((a, b) => b[0].localeCompare(a[0]))   // most recent date first
    .map(([date, e]) => `<tr><td>${escapeHtml(date)}</td><td>${e?.puzzleNumber ?? '—'}</td><td>${e?.moves ?? '—'}</td><td>${escapeHtml(fmtDailyTime(e))}</td><td>${e?.hintsUsed ?? 0}</td></tr>`)
    .join('')
  const dailiesBlock = dailyRows
    ? `<table><thead><tr><th>Date</th><th>Puzzle #</th><th>Hops</th><th>Time</th><th>Hints Used</th></tr></thead><tbody>${dailyRows}</tbody></table>`
    : '<p class="empty">No completed dailies yet.</p>'

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Action! — Your Data Report</title>
<style>
:root{--bg:#0F0F14;--text:#F5EED8;--gold:#D4AF5A;--muted:#9b9482;--card:#191921;--border:#2b2b35;}
*{box-sizing:border-box;}
body{margin:0;padding:40px 20px;background:var(--bg);color:var(--text);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;line-height:1.5;}
.report{max-width:820px;margin:0 auto;}
header{text-align:center;border-bottom:2px solid var(--gold);padding-bottom:24px;margin-bottom:36px;}
header h1{margin:0 0 6px;color:var(--gold);font-size:32px;letter-spacing:.5px;}
header h2{margin:0 0 10px;font-size:18px;font-weight:500;}
.gen-date{color:var(--muted);font-size:13px;}
section{margin-bottom:36px;}
h3{margin:0 0 18px;color:var(--gold);font-size:15px;text-transform:uppercase;letter-spacing:1.2px;border-bottom:1px solid var(--border);padding-bottom:8px;}
h4{margin:24px 0 10px;color:var(--gold);font-size:14px;}
dl.kv{display:grid;grid-template-columns:160px 1fr;gap:10px 16px;margin:0;}
dl.kv dt{color:var(--muted);}
dl.kv dd{margin:0;}
.stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;}
.stat{background:var(--card);border:1px solid var(--border);border-radius:10px;padding:18px;text-align:center;}
.stat .num{color:var(--gold);font-size:30px;font-weight:700;line-height:1;}
.stat .lbl{margin-top:8px;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.6px;}
ul.themes{list-style:none;margin:14px 0 0;padding:0;display:flex;flex-wrap:wrap;gap:8px;}
ul.themes li{padding:5px 14px;border:1px solid var(--gold);border-radius:999px;color:var(--gold);font-size:13px;}
ul.themes li.empty-inline{border-color:var(--border);color:var(--muted);}
table{width:100%;border-collapse:collapse;font-size:14px;}
th,td{padding:10px 12px;text-align:left;border-bottom:1px solid var(--border);}
th{color:var(--gold);font-size:11px;text-transform:uppercase;letter-spacing:.6px;}
.empty{color:var(--muted);font-style:italic;}
footer{margin-top:48px;padding-top:20px;border-top:1px solid var(--border);text-align:center;color:var(--muted);font-size:12px;}
@media print{
body{background:#fff;color:#1a1a1a;padding:0;}
.report{max-width:none;}
header{border-color:#000;}
header h1,h3,h4,.stat .num,ul.themes li,th{color:#000 !important;}
.gen-date,dl.kv dt,.stat .lbl,.empty,footer{color:#555 !important;}
.stat,ul.themes li{background:#fff !important;border-color:#999 !important;}
th,td{border-color:#bbb !important;}
}
</style>
</head>
<body>
<div class="report">
<header>
<h1>Action! The Movie Game</h1>
<h2>Your Data Report</h2>
<div class="gen-date">Generated ${escapeHtml(genDate)}</div>
</header>
<section>
<h3>Account</h3>
<dl class="kv">
<dt>Username</dt><dd>${escapeHtml(data.username || '—')}</dd>
<dt>Email</dt><dd>${escapeHtml(data.email || '—')}</dd>
<dt>Member since</dt><dd>${escapeHtml(fmtDate(data.createdAt))}</dd>
<dt>Last seen</dt><dd>${escapeHtml(fmtDateTime(data.lastSeen))}</dd>
</dl>
</section>
<section>
<h3>Stats</h3>
<div class="stats-grid">
<div class="stat"><div class="num">${num(data.streak)}</div><div class="lbl">Current Streak</div></div>
<div class="stat"><div class="num">${num(data.streakLongest)}</div><div class="lbl">Longest Streak</div></div>
<div class="stat"><div class="num">${num(data.tickets)}</div><div class="lbl">Tickets</div></div>
</div>
<h4>Themes Unlocked</h4>
<ul class="themes">${themeItems}</ul>
</section>
<section>
<h3>Completed Dailies</h3>
${dailiesBlock}
</section>
<footer>Downloaded from actionthemoviegame.com on ${escapeHtml(genDate)}</footer>
</div>
</body>
</html>`
}

// ─── Main ProfileScreen ───────────────────────────────────────────────────────
export default function ProfileScreen({
  user,
  currentUser,
  activeTheme = 'extra',
  streak,
  tickets = 0,
  todayDate,
  unlockedThemes = ['extra'],
  onSetTheme,
  onPurchaseTheme,
  onUpdateUser,
  onSignIn,
  onSignOut,
  onDeleteAccount,
  onBack,
}) {
  // ── Avatar ──────────────────────────────────────────────────────────────────
  const fileRef = useRef(null)
  const [cropSrc, setCropSrc] = useState(null)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState(user?.username || '')

  const handlePickFile = () => fileRef.current?.click()
  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setCropSrc(reader.result)
    reader.readAsDataURL(file)
    e.target.value = ''
  }
  const handleCropSave = (dataUrl) => {
    onUpdateUser?.({ avatar: dataUrl })
    setCropSrc(null)
  }
  const handleRemoveAvatar = () => onUpdateUser?.({ avatar: null })

  const handleNameSave = () => {
    const trimmed = nameDraft.trim().slice(0, 25)
    onUpdateUser?.({ username: trimmed || null })
    setEditingName(false)
  }

  // ── Stats ───────────────────────────────────────────────────────────────────
  const completions = useMemo(() => getDailyCompletions(), [])
  const totalPlayed = Object.keys(completions).length

  const allMoves = useMemo(
    () => Object.values(completions).map((c) => c.moves).filter(Boolean),
    [completions],
  )
  const bestScore = allMoves.length > 0 ? Math.min(...allMoves) : '--'

  // ── Chart data (last 7 days) ─────────────────────────────────────────────
  const last7Dates = useMemo(() => {
    if (!todayDate) return []
    const dates = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date(todayDate + 'T00:00:00')
      d.setDate(d.getDate() - i)
      dates.push(d.toISOString().slice(0, 10))
    }
    return dates
  }, [todayDate])

  const playedMoves = last7Dates
    .filter((date) => completions[date])
    .map((date) => completions[date].moves)

  const maxMoves = playedMoves.length > 0 ? Math.max(...playedMoves, 1) : 5
  const avgMoves = playedMoves.length > 0
    ? (playedMoves.reduce((a, b) => a + b, 0) / playedMoves.length).toFixed(1)
    : '--'

  const currentStreak = streak?.currentStreak ?? 0
  const longestStreak = streak?.longestStreak ?? 0

  // ── Account section state ─────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const username = user?.username || (currentUser?.email?.split('@')[0]) || 'Guest'
  const avatar   = user?.avatar
  const joined   = formatJoined(user?.joinedDate)

  // ── App version (live; long-press to copy) ──────────────────────────────
  const [version, setVersion] = useState('')
  const [copied, setCopied] = useState(false)
  const versionPressTimer = useRef(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      // Native: @capacitor/app (once installed + cap-synced) registers `App` on
      // the Capacitor bridge — the same native call App.getInfo() makes, giving
      // version + build. Falls through if the plugin isn't present.
      try {
        if (Capacitor?.isNativePlatform?.() && Capacitor?.Plugins?.App) {
          const info = await Capacitor.Plugins.App.getInfo()
          if (alive && info?.version) { setVersion(`Action! v${info.version} (${info.build})`); return }
        }
      } catch { /* fall through to the web env var */ }
      // Web (and native before the plugin is synced): build-time env var.
      const v = import.meta.env.VITE_APP_VERSION
      if (alive && v) setVersion(`Action! v${v}`)
    })()
    return () => { alive = false }
  }, [])

  const copyVersion = async () => {
    if (!version) return
    try {
      await navigator.clipboard.writeText(version)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard unavailable — silent */ }
  }
  // Press-and-hold (touch or mouse) → copy, ~500ms.
  const startVersionPress = () => {
    clearTimeout(versionPressTimer.current)
    versionPressTimer.current = setTimeout(copyVersion, 500)
  }
  const cancelVersionPress = () => clearTimeout(versionPressTimer.current)

  // Export the signed-in user's Firestore data as a styled, printable HTML report.
  const handleDownloadData = async () => {
    if (!currentUser?.uid || downloading) return
    setDownloading(true)
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid))
      const data = snap.exists() ? snap.data() : {}
      const now = new Date()
      const blob = new Blob([buildReportHtml(data, now)], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const date = todayDate || now.toISOString().slice(0, 10)
      const safeName =
        (data.username || username || 'user')
          .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'user'
      const a = document.createElement('a')
      a.href = url
      a.download = `action-report-${safeName}-${date}.html`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[DownloadData] failed:', err)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="profile-screen">
      <div className="screen-content">

      {/* ── Header ── */}
      <div className="profile-header">
        {/* Avatar */}
        <div className="profile-header__avatar-wrap">
          <div className="profile-header__avatar" onClick={handlePickFile} title="Change photo">
            <img
              src={avatar || '/clapperboard.png'}
              alt=""
              onError={(e) => { if (!e.currentTarget.src.endsWith('/clapperboard.png')) e.currentTarget.src = '/clapperboard.png' }}
            />
            <div className="profile-header__avatar-overlay"><IconCamera size={24} color="#fff" /></div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
        </div>
        {avatar && (
          <button
            type="button"
            className="profile-header__avatar-remove"
            onClick={handleRemoveAvatar}
          >
            Remove photo
          </button>
        )}

        {/* Name */}
        {editingName ? (
          <div className="profile-header__name-edit">
            <input
              className="profile-header__name-input"
              type="text"
              value={nameDraft}
              maxLength={25}
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
            />
            <button type="button" className="btn-primary" onClick={handleNameSave}>Save</button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setNameDraft(user?.username || ''); setEditingName(false) }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="profile-header__name-row">
            <h2 className="profile-header__name">{username}</h2>
            <button
              type="button"
              className="profile-header__name-edit-btn"
              onClick={() => { setNameDraft(user?.username || ''); setEditingName(true) }}
              aria-label="Edit username"
            >
              <IconPencil size={16} color="var(--accent-gold)" />
            </button>
          </div>
        )}

        {/* Joined date */}
        {joined && <p className="profile-header__joined">Joined {joined}</p>}

        {/* Streak */}
        <div className="profile-header__streaks">
          <span className="profile-header__streak">
            <IconFlame size={15} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /> {currentStreak} day streak
          </span>
          <span className="profile-header__streak-divider">·</span>
          <span className="profile-header__streak profile-header__streak--muted">
            Best: {longestStreak}
          </span>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="profile-stats">
        <div className="profile-stat">
          <span className="profile-stat__value">{totalPlayed}</span>
          <span className="profile-stat__label">Games Played</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat__value">{bestScore}</span>
          <span className="profile-stat__label">Best Score</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat__value">{avgMoves}</span>
          <span className="profile-stat__label">Avg. Hops</span>
        </div>
      </div>

      {/* ── Box Office Numbers chart ── */}
      {last7Dates.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section__label">Box Office Numbers <IconClapper size={15} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /></h3>
          <BarChart
            dates={last7Dates}
            completions={completions}
            todayDate={todayDate}
            maxMoves={maxMoves}
          />
        </div>
      )}

      {/* ── Your Hollywood Career (themes) ── */}
      <section className="settings-section">
        <h3 className="settings-section__title">Your Hollywood Career</h3>
        <p className="settings-streak-line"><IconFlame size={16} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /> Current Streak: <strong>{currentStreak}</strong> day{currentStreak !== 1 ? 's' : ''}</p>

        {/* The "next target" theme — the lowest-ordered locked theme. The
            streak-progress counter is shown ONLY on that card so the user
            sees progress toward the specific next unlock, not the same
            counter repeated on every locked tier. Matches what the
            daily-completion handler awards at the next 7-day milestone. */}
        <div className="theme-list">
          {THEMES.map((theme) => {
            const isUnlocked = unlockedThemes.includes(theme.id)
            const isActive   = activeTheme === theme.id
            const isNextTarget = !isUnlocked && theme.id === getNextLockedThemeId(unlockedThemes)
            // Streak progress within the current 7-day unlock cycle.
            // streak=5  → 5/7 (5 days into the cycle).
            // streak=12 → 5/7 (12 mod 7 = 5).
            // streak=14 → 0/7 (just unlocked a tier, fresh cycle starts).
            const cycleProgress = currentStreak % 7

            return (
              <div
                key={theme.id}
                data-theme={theme.id}
                className={`theme-card${isUnlocked ? ' theme-card--unlocked' : ' theme-card--locked'}`}
              >
                <div className="theme-card__left">
                  {isUnlocked && (
                    <div className="theme-card__swatch">
                      <div className="theme-card__swatch-dot" />
                    </div>
                  )}
                  {!isUnlocked && (
                    <div className="theme-card__lock"><IconLock size={18} /></div>
                  )}
                </div>

                <div className="theme-card__body">
                  <span className="theme-card__name">{theme.name}</span>
                  <span className="theme-card__desc">{theme.description}</span>
                  {isUnlocked && theme.streakRequired > 0 && (
                    <span className="theme-card__badge"><IconFlame size={14} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /> Permanently unlocked</span>
                  )}
                  {isUnlocked && theme.streakRequired === 0 && (
                    <span className="theme-card__badge"><IconClapper size={14} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /> Always available</span>
                  )}
                  {!isUnlocked && (
                    <span className="theme-card__locked-hint">
                      7-day streak · or · 100 <IconTicket size={14} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} />
                    </span>
                  )}
                  {isNextTarget && (
                    <span className="theme-card__progress">
                      {cycleProgress}/7 days
                    </span>
                  )}
                </div>

                <div className="theme-card__action">
                  {isUnlocked && isActive && (
                    <span className="theme-card__active">Active <IconCheck size={14} color="var(--accent-gold)" style={{ verticalAlign: 'middle' }} /></span>
                  )}
                  {isUnlocked && !isActive && (
                    <button
                      type="button"
                      className="btn-ghost theme-card__apply"
                      onClick={() => onSetTheme?.(theme.id)}
                    >
                      Apply
                    </button>
                  )}
                  {!isUnlocked && (
                    <button
                      type="button"
                      className="theme-card__buy"
                      onClick={() => onPurchaseTheme?.(theme.id)}
                      aria-label={`Unlock ${theme.name} for 100 tickets`}
                    >
                      Unlock for 100 <IconTicket size={12} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── Account ── */}
      <section className="settings-section">
        <h3 className="settings-section__title">Account</h3>
        {currentUser ? (
          <div className="settings-account">
            <p className="settings-account__email">{currentUser.email}</p>

            {/* Utility pair — side by side */}
            <div className="settings-account__row">
              <button type="button" className="account-btn account-btn--primary" onClick={onSignOut}>
                <IconSignOut size={17} /> Sign Out
              </button>
              <button
                type="button"
                className="account-btn account-btn--ghost"
                onClick={handleDownloadData}
                disabled={downloading}
              >
                <IconDownload size={17} /> {downloading ? 'Downloading…' : 'Download Data'}
              </button>
            </div>

            {!deleteConfirm ? (
              <>
                <div className="settings-account__divider" />
                <button
                  type="button"
                  className="account-btn account-btn--danger"
                  onClick={() => setDeleteConfirm(true)}
                >
                  <IconTrash size={16} /> Delete Account
                </button>
              </>
            ) : (
              <div className="settings-account__delete-confirm">
                <p className="settings-account__delete-warning">
                  This will permanently delete your account and all data. This cannot be undone.
                </p>
                <div className="settings-account__delete-actions">
                  <button type="button" className="account-btn account-btn--ghost" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="account-btn account-btn--danger-solid"
                    disabled={deleting}
                    onClick={async () => { setDeleting(true); await onDeleteAccount() }}
                  >
                    {deleting ? 'Deleting…' : 'Delete Forever'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="settings-account">
            <p className="settings-account__guest">You&apos;re playing as a guest.</p>
            <button type="button" className="btn-primary settings-account__signin" onClick={onSignIn}>
              Sign In / Create Account
            </button>
          </div>
        )}
      </section>

      {version && (
        <p
          className="app-version"
          onPointerDown={startVersionPress}
          onPointerUp={cancelVersionPress}
          onPointerLeave={cancelVersionPress}
          onPointerCancel={cancelVersionPress}
        >
          {copied ? 'Copied' : version}
        </p>
      )}

      </div>{/* end screen-content */}

      {/* ── AvatarCropper (conditionally rendered) ── */}
      {cropSrc && (
        <AvatarCropper
          imageSrc={cropSrc}
          onSave={handleCropSave}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </div>
  )
}
